import { toast } from '@heroui/react'
import { create } from 'zustand'

import type { GetUsersParams } from '@/api/users.api'
import { usersApi } from '@/api/users.api'
import i18n from '@/i18n'
import type { CreateUserRequest, Role,UpdateUserRequest, User as ApiUser } from '@/types/user'
import { getAppSettings, updateAppSettings } from '@/utils/settings'
import { SortDirection, sorting, type TableSort } from '@/utils/sorting'

export interface UiUser {
  id: string;
  name: string;
  email: string;
  phone: string;
  role: Role;
  verified?: boolean;
}

interface UsersStore {
  users: UiUser[];
  isLoading: boolean;
  error: string | null;
  filters: {
    name: string;
    email: string;
  };
  pagination: {
    sort: TableSort | null;
    page: number;
    limit: number;
    totalPages: number;
    total: number;
  };

  fetchUsers: () => Promise<void>;
  addUser: (u: Omit<UiUser, 'id'>) => Promise<UiUser>;
  updateUser: (id: string, patch: Partial<Omit<UiUser, 'id'>>) => Promise<void>;
  deleteUser: (id: string) => Promise<void>;

  setFilters: (filters: Partial<UsersStore['filters']>) => void;
  setPagination: (pagination: Partial<UsersStore['pagination']>) => void;
  resetFilters: () => void;
  clearError: () => void;
}

const mapApiToUi = (u: ApiUser): UiUser => ({
  id: u.ID,
  name: u.Name,
  email: u.Email,
  phone: u.Phone,
  role: u.Role,
  verified: u.Verified,
})

const mapUiToApiCreate = (u: Omit<UiUser, 'id'>): CreateUserRequest => ({
  Name: u.name,
  Email: u.email,
  Phone: u.phone || '',
  Role: u.role,
})

const mapUiToApiUpdate = (u: Partial<Omit<UiUser, 'id'>>): UpdateUserRequest => ({
  Name: u.name,
  Email: u.email,
  Phone: u.phone,
  Role: u.role,
})

export const useUsersStore = create<UsersStore>((set, get) => ({
  users: [],
  isLoading: false,
  error: null,
  filters: {
    name: '',
    email: '',
  },
  pagination: {
    sort: { key: 'name', direction: SortDirection.ASC },
    page: 0,
    limit: getAppSettings().usersLimit ?? 10,
    totalPages: 0,
    total: 0,
  },

  fetchUsers: async () => {
    const { filters, pagination } = get()
    set({ isLoading: true, error: null })

    const params: GetUsersParams = {
      name: filters.name || undefined,
      email: filters.email || undefined,
      page: pagination.page,
      limit: pagination.limit,
      sort: pagination.sort ? [sorting(pagination.sort)] : undefined,
    }

    try {
      const response = await usersApi.getUsers(params)
      set({
        users: response.Users.map(mapApiToUi),
        pagination: {
          ...pagination,
          totalPages: Math.ceil(response.Pagination.Total / pagination.limit),
          total: response.Pagination.Total,
        },
      })
    } catch (err) {
      const message = (err as Error).message
      set({ error: message })
      toast.danger(i18n.t('users.toast.fetch_error'), { description: message })
    } finally {
      set({ isLoading: false })
    }
  },

  addUser: async u => {
    set({ isLoading: true, error: null })
    try {
      const apiUser = await usersApi.createUser(mapUiToApiCreate(u))
      const uiUser = mapApiToUi(apiUser)
      toast.success(i18n.t('users.toast.create_success'), { 
        description: i18n.t('users.toast.create_success_desc') 
      })

      await get().fetchUsers()

      return uiUser
    } catch (err) {
      toast.danger(i18n.t('users.toast.create_error'), { description: (err as Error).message })
      set({ error: (err as Error).message })
      throw err
    } finally {
      set({ isLoading: false })
    }
  },

  updateUser: async (id, patch) => {
    set({ isLoading: true, error: null })
    try {
      await usersApi.updateUser(id, mapUiToApiUpdate(patch))
      toast.success(i18n.t('users.toast.update_success'), { 
        description: i18n.t('users.toast.update_success_desc') 
      })

      await get().fetchUsers()
    } catch (err) {
      toast.danger(i18n.t('users.toast.update_error'), { description: (err as Error).message })
      set({ error: (err as Error).message })
      throw err
    } finally {
      set({ isLoading: false })
    }
  },

  deleteUser: async id => {
    set({ isLoading: true, error: null })
    try {
      await usersApi.deleteUser(id)
      toast.success(i18n.t('users.toast.delete_success'), { 
        description: i18n.t('users.toast.delete_success_desc') 
      })

      await get().fetchUsers()
    } catch (err) {
      toast.danger(i18n.t('users.toast.delete_error'), { description: (err as Error).message })
      set({ error: (err as Error).message })
      throw err
    } finally {
      set({ isLoading: false })
    }
  },

  setFilters: newFilters => {
    set(state => ({
      filters: { ...state.filters, ...newFilters },
      pagination: { ...state.pagination, page: 0 },
    }))
    get().fetchUsers()
  },

  setPagination: newPagination => {
    set(state => {
      const updatedPagination = { ...state.pagination, ...newPagination }
      if (newPagination.limit !== undefined) {
        updateAppSettings({ usersLimit: newPagination.limit })
      }
      return { pagination: updatedPagination }
    })
    get().fetchUsers()
  },

  resetFilters: () => {
    const initialLimit = getAppSettings().usersLimit ?? 10
    set({
      filters: {
        name: '',
        email: '',
      },
      pagination: {
        sort: { key: 'name', direction: SortDirection.ASC },
        page: 0,
        limit: initialLimit,
        totalPages: 1,
        total: 0,
      },
    })
    get().fetchUsers()
  },

  clearError: () => set({ error: null }),
}))
